import { FC } from 'react';

import physicalBasicRebrandedPaymentCardImg from 'assets/images/paymentCards/rebranded/basic.png';
import physicalPremiumRebrandedPaymentCardImg from 'assets/images/paymentCards/rebranded/premium.png';
import virtualRebrandedPaymentCardImg from 'assets/images/paymentCards/rebranded/virtual.png';
import virtualTemporaryRebrandedPaymentCardImg from 'assets/images/paymentCards/rebranded/virtualTemp.png';
import SunshineCard from 'components/_core/SunshineCard';
import usePaymentCardOrder from 'features/PaymentCards/hooks/usePaymentCardOrder';
import { PaymentCardType } from 'types/creditCard';

import * as routes from '../../../../routes';
import locales from '../../locales';
import Card, { CardType } from './components/Card';

const CardList: FC = () => {
  const { canOrderPhysicalCard, canOrderPremiumCard, pricingWithoutVAT } =
    usePaymentCardOrder();

  const physicalCardPriceLabel = (() => {
    if (!canOrderPhysicalCard) {
      return locales.prices.alreadyActivated;
    }

    if (pricingWithoutVAT.physicalCardPrice === 0) {
      return locales.prices.free;
    }

    return locales.formatString(locales.prices.physicalCardCosts, {
      cardPrice: String(pricingWithoutVAT.physicalCardPrice),
      monthlyFee: String(pricingWithoutVAT.monthlyFee),
    }) as string;
  })();

  const virtualCardPriceLabel = (() => {
    if (pricingWithoutVAT.virtualCardPrice === 0) {
      return locales.prices.free;
    }

    return locales.formatString(locales.prices.virtualCardCost, {
      cardPrice: String(pricingWithoutVAT.virtualCardPrice),
    }) as string;
  })();

  const getPhysicalCardImgAsset = () => {
    if (canOrderPremiumCard) {
      return physicalPremiumRebrandedPaymentCardImg;
    }

    return physicalBasicRebrandedPaymentCardImg;
  };

  const getVirtualCardImgAsset = () => {
    return virtualRebrandedPaymentCardImg;
  };

  const getVirtualTemporaryCardImgAsset = () => {
    return virtualTemporaryRebrandedPaymentCardImg;
  };

  const cardList: CardType[] = [
    {
      description:
        locales.options[PaymentCardType.TEMPORARY_VIRTUAL].description,
      disabled: false,
      id: 'virtual-temporary-card',
      image: {
        src: getVirtualTemporaryCardImgAsset(),
      },
      infoTag: {
        text: locales.badge,
        type: 'INSTANT',
      },
      label: virtualCardPriceLabel,
      title: locales.formatString(
        locales.options[PaymentCardType.TEMPORARY_VIRTUAL].title,
      ),
      to: routes.orderNewVirtualTemporary,
    },
    {
      description: locales.options[PaymentCardType.VIRTUAL].description,
      disabled: false,
      id: 'virtual-permanent-card',
      image: {
        src: getVirtualCardImgAsset(),
      },
      infoTag: {
        text: locales.badge,
        type: 'INSTANT',
      },
      label: virtualCardPriceLabel,
      title: locales.formatString(
        locales.options[PaymentCardType.VIRTUAL].title,
      ),
      to: routes.orderNewVirtual,
    },
    {
      description: locales.options[PaymentCardType.PHYSICAL].description,
      disabled: !canOrderPhysicalCard,
      id: 'physical-card',
      image: {
        opacity: canOrderPhysicalCard ? 1 : 0.5,
        src: getPhysicalCardImgAsset(),
      },
      infoTag: {
        text: locales.deliveryPeriod,
        type: 'TIMER',
      },
      label: physicalCardPriceLabel,
      title: locales.formatString(
        locales.options[PaymentCardType.PHYSICAL].title,
      ) as string,
      to: canOrderPhysicalCard ? routes.orderNewPhysical : routes.orderNew,
    },
  ];

  return (
    <SunshineCard.Group border="none">
      {cardList.map((card) => {
        return <Card card={card} key={card.id} />;
      })}
    </SunshineCard.Group>
  );
};

export default CardList;
